import { useTheme } from '@/hooks/useTheme';

export default function Footer() {
  const { colors } = useTheme();

  const footerLinks = {
    company: [
      { name: 'About Us', href: '#about' },
      { name: 'Our Story', href: '#' },
      { name: 'Careers', href: '#' },
      { name: 'Press', href: '#' },
    ],
    support: [
      { name: 'Help Center', href: '#' },
      { name: 'Size Guide', href: '#' },
      { name: 'Returns', href: '#' },
      { name: 'Track Order', href: '/profile?tab=orders' },
    ],
    legal: [
      { name: 'Privacy Policy', href: '#' },
      { name: 'Terms of Service', href: '#' },
      { name: 'Cookie Policy', href: '#' },
    ],
  };

  const socials = [
    { icon: 'ri-instagram-line', href: '#', label: 'Instagram' },
    { icon: 'ri-facebook-line', href: '#', label: 'Facebook' },
    { icon: 'ri-whatsapp-line', href: '#', label: 'WhatsApp' },
    { icon: 'ri-twitter-x-line', href: '#', label: 'Twitter' },
  ];

  // Dynamic gradient based on theme
  const getGradient = () => {
    return `linear-gradient(90deg, ${colors.primary}, ${colors.secondary}, ${colors.primary})`;
  };

  return (
    <footer className="relative overflow-hidden dark:bg-slate-900" style={{ backgroundColor: colors.bg === '#FFFFFF' ? '#0A0A0A' : colors.bg }}>
      {/* Top gradient line */}
      <div className="h-1 w-full" style={{ background: getGradient() }}></div>

      {/* Main Footer */}
      <div className="w-full px-4 sm:px-6 lg:px-8 xl:px-12 pt-14 pb-10">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-8 lg:gap-12">

            {/* Brand Column */}
            <div className="col-span-2 md:col-span-4 lg:col-span-2">
              <a href="#home" className="flex items-center gap-2.5 mb-5 group">
                <div 
                  className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})` }}
                >
                  <i className="ri-t-shirt-line text-white text-xl"></i>
                </div>
                <div className="flex flex-col leading-none">
                  <span className="text-[17px] font-black">
                    <span 
                      className="text-transparent bg-clip-text"
                      style={{ backgroundImage: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})` }}
                    >Clothy</span>
                    <span className="text-white">Bazar</span>
                  </span>
                  <span 
                    className="text-[10px] font-semibold mt-0.5"
                    style={{ color: colors.primary }}
                  >⚡ 30 Min Delivery</span>
                </div>
              </a>
              <p className="text-gray-400 text-sm leading-relaxed mb-5 max-w-xs">
                Dehradun's #1 fashion destination. Premium streetwear with lightning-fast 30-minute delivery, Smart Size Exchange, and Cash on Delivery.
              </p>
              <div className="flex gap-2 mb-5">
                {socials.map((s) => (
                  <a 
                    key={s.label} 
                    href={s.href} 
                    aria-label={s.label}
                    className="w-9 h-9 rounded-xl bg-white/8 border border-white/10 flex items-center justify-center text-gray-400 transition-all hover:text-white"
                    style={{ 
                      backgroundColor: `${colors.primary}15`,
                      borderColor: `${colors.primary}30`,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = colors.primary;
                      e.currentTarget.style.borderColor = colors.primary;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = `${colors.primary}15`;
                      e.currentTarget.style.borderColor = `${colors.primary}30`;
                    }}
                  >
                    <i className={`${s.icon} text-base`}></i>
                  </a>
                ))}
              </div>

              {/* Quick stats */}
              <div className="flex gap-4">
                {[
                  { num: '50K+', label: 'Customers' },
                  { num: '30m', label: 'Delivery' },
                  { num: '4.8★', label: 'Rating' },
                ].map(s => (
                  <div key={s.label}>
                    <p className="text-white font-black text-base">{s.num}</p>
                    <p className="text-gray-500 text-xs">{s.label}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Company */}
            <div>
              <h4 className="text-white font-black text-sm uppercase tracking-wider mb-5">Company</h4>
              <ul className="space-y-3">
                {footerLinks.company.map((link) => (
                  <li key={link.name}>
                    <a 
                      href={link.href} 
                      className="text-gray-400 text-sm transition-colors hover:text-white"
                      style={{ 
                        color: `${colors.primary}99`,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = colors.primary;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = `${colors.primary}99`;
                      }}
                    >{link.name}</a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Support */}
            <div>
              <h4 className="text-white font-black text-sm uppercase tracking-wider mb-5">Support</h4>
              <ul className="space-y-3">
                {footerLinks.support.map((link) => (
                  <li key={link.name}>
                    <a 
                      href={link.href} 
                      className="text-gray-400 text-sm transition-colors hover:text-white"
                      style={{ 
                        color: `${colors.primary}99`,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = colors.primary;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = `${colors.primary}99`;
                      }}
                    >{link.name}</a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Newsletter */}
            <div className="col-span-2 md:col-span-4 lg:col-span-1">
              <h4 className="text-white font-black text-sm uppercase tracking-wider mb-5">Stay Updated</h4>
              <p className="text-gray-400 text-sm mb-4">Get exclusive offers and new arrivals in your inbox.</p>
              <div className="flex gap-2 mb-4">
                <input
                  type="email"
                  placeholder="Your email"
                  className="flex-1 px-3 py-2.5 rounded-xl bg-white/8 border border-white/10 text-white text-sm placeholder-gray-500 focus:outline-none transition-all"
                  style={{ 
                    backgroundColor: `${colors.primary}10`,
                    borderColor: `${colors.primary}30`,
                  }}
                />
                <button 
                  className="px-4 py-2.5 rounded-xl text-white cursor-pointer hover:opacity-90 transition-all"
                  style={{ background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})` }}
                >
                  <i className="ri-send-plane-fill"></i>
                </button>
              </div>
              {/* Legal */}
              <ul className="space-y-2">
                {footerLinks.legal.map((link) => (
                  <li key={link.name}>
                    <a 
                      href={link.href} 
                      className="text-gray-500 text-xs transition-colors hover:text-gray-400"
                      style={{ 
                        color: `${colors.primary}80`,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = colors.primary;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = `${colors.primary}80`;
                      }}
                    >{link.name}</a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/8 px-4 sm:px-6 lg:px-8 xl:px-12 py-5" style={{ borderColor: `${colors.primary}15` }}>
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-gray-500 text-sm text-center">
            © 2026 Clothy Bazar. All rights reserved. Made with ❤️ in Dehradun
          </p>
          <div className="flex items-center gap-4">
            <span 
              className="text-xs flex items-center gap-1.5"
              style={{ color: `${colors.primary}99` }}
            >
              <i className="ri-map-pin-fill text-xs" style={{ color: colors.primary }}></i>Dehradun, Uttarakhand
            </span>
            <span 
              className="text-xs flex items-center gap-1.5"
              style={{ color: `${colors.primary}99` }}
            >
              <i className="ri-flashlight-fill text-xs" style={{ color: colors.primary }}></i>30 Min Delivery
            </span>
          </div>
        </div>
      </div>

      {/* Big Brand Text */}
      <div className="overflow-hidden py-6 border-t border-white/4" style={{ borderColor: `${colors.primary}10` }}>
        <div 
          className="font-black text-[80px] md:text-[140px] text-center whitespace-nowrap select-none tracking-tighter"
          style={{ color: `${colors.primary}08` }}
        >
          CLOTHY BAZAR
        </div>
      </div>
    </footer>
  );
}
